import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

const AddLabTestDialog = ({ open, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    date_performed: new Date().toISOString().split('T')[0],
    reason: '',
    results_summary: '',
    complications: '',
    follow_up_required: false,
    follow_up_date: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onAdd({
        ...formData,
        results_summary: formData.results_summary || undefined,
        complications: formData.complications || undefined,
        follow_up_date: formData.follow_up_date || undefined,
        notes: formData.notes || undefined,
      });

      setFormData({
        name: '',
        date_performed: new Date().toISOString().split('T')[0],
        reason: '',
        results_summary: '',
        complications: '',
        follow_up_required: false,
        follow_up_date: '',
        notes: '',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Lab Test</DialogTitle>
          <DialogDescription>Record diagnostic test information</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Test Name *</Label>
              <Input id="name" placeholder="e.g., Blood Test, MRI, X-Ray" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="date_performed">Date Performed *</Label>
                <Input id="date_performed" type="date" value={formData.date_performed} onChange={(e) => setFormData({ ...formData, date_performed: e.target.value })} required />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="reason">Reason for Test *</Label>
              <Input id="reason" value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} required />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="results_summary">Results Summary</Label>
              <Textarea id="results_summary" value={formData.results_summary} onChange={(e) => setFormData({ ...formData, results_summary: e.target.value })} rows={3} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="complications">Complications (if any)</Label>
              <Textarea id="complications" value={formData.complications} onChange={(e) => setFormData({ ...formData, complications: e.target.value })} rows={2} />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="follow_up_required"
                checked={formData.follow_up_required}
                onCheckedChange={(checked) => setFormData({ ...formData, follow_up_required: checked })}
              />
              <Label htmlFor="follow_up_required" className="font-normal cursor-pointer">Follow-up required</Label>
            </div>

            {formData.follow_up_required && (
              <div className="grid gap-2">
                <Label htmlFor="follow_up_date">Follow-up Date</Label>
                <Input id="follow_up_date" type="date" value={formData.follow_up_date} onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })} />
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
              {loading ? 'Adding...' : 'Add Lab Test'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddLabTestDialog;